import * as React from 'react';
import { createRoot, Root } from 'react-dom/client';
import { CUIChatWidget, BotifyChatWidgetProps } from './src/widget-app';
import type { WidgetVariant } from './variants/types';
import { mountFrameBridge } from './src/embed/frame-bridge';
import { whenHostConfigReady } from './src/embed/wait-for-config';

// Injected at build time via vite `define`
declare const PACKAGE_VERSION_TO_BE_REPLACED: string;

export type EmbedConfig = BotifyChatWidgetProps & {
    language?: string;
    embedded?: boolean;
    connectOn?: string;
    mountTo?: string;
    customData?: Record<string, any>;
    /** Run as the bridge inside the hosted iframe-loader frame page instead of mounting directly. */
    frameMode?: boolean;
    /** Base URL of the hosted frame page the host-side loader points its iframe at. */
    frameBaseUrl?: string;
};

declare global {
    interface Window {
        CUIWidgetConfig?: EmbedConfig;
        CUIWidgetDestroy?: () => void;
        CUIWidgetInfo?: { variant: string; version: string; integration?: 'direct' | 'iframe' };
        CUIWidgetRemount?: () => void;
    }
}

let root: Root | null = null;
let bridge: { destroy: () => void } | null = null;
let currentVariant: WidgetVariant | undefined;

/**
 * Merge the variant's baked-in defaults under the host page's
 * window.CUIWidgetConfig. The host page always wins; the variant only fills
 * gaps. styleTheme is merged one level deep so a host page can override
 * single theme values without losing the variant's default themeName.
 */
export function resolveConfig(variant?: WidgetVariant): EmbedConfig {
    const host = window.CUIWidgetConfig || {};

    if (!variant) {
        return host;
    }

    return {
        ...variant.defaultConfig,
        ...host,
        styleTheme: {
            themeName: variant.theme.themeName,
            ...variant.defaultConfig.styleTheme,
            ...host.styleTheme,
        },
    };
}

function mount(variant?: WidgetVariant) {
    currentVariant = variant;
    if (root || bridge) {
        window.CUIWidgetDestroy?.();
    }

    const config = resolveConfig(variant);

    if (config.frameMode && window.parent === window) {
        // The hosted frame page opened top-level (not inside the loader's
        // iframe): there is no host to bridge to, and mounting directly would
        // run the variant's partner chat on a page no frame-ancestors rule covers.
        console.warn('[CUI Widget] frameMode is set but the page is not framed — not mounting');
        return;
    }

    if (config.frameMode) {
        const container = document.createElement('div');
        container.id = 'cui-widget-embed-root';
        container.style.position = 'fixed';
        container.style.width = '0';
        container.style.height = '0';
        container.style.overflow = 'visible';
        container.style.zIndex = '2147483647';
        document.body.appendChild(container);
        bridge = mountFrameBridge({
            container,
            variant,
            version: PACKAGE_VERSION_TO_BE_REPLACED,
            resolveConfig: (hostOverrides) => {
                const saved = window.CUIWidgetConfig;
                // host overrides from the loader sit ABOVE the frame page's own config
                window.CUIWidgetConfig = { ...(saved || {}), ...(hostOverrides as EmbedConfig) };
                const merged = resolveConfig(variant);
                window.CUIWidgetConfig = saved;
                delete (merged as EmbedConfig).frameMode;
                return merged;
            },
        });
        window.CUIWidgetInfo = {
            variant: variant ? variant.name : 'legacy',
            version: PACKAGE_VERSION_TO_BE_REPLACED,
            integration: variant?.integration || 'direct',
        };
        window.CUIWidgetDestroy = () => {
            if (bridge) {
                bridge.destroy();
                bridge = null;
            }
            container.remove();
            delete window.CUIWidgetDestroy;
        };
        return;
    }

    let container: HTMLElement;
    let createdContainer = false;

    if (config.mountTo) {
        const el = document.querySelector(config.mountTo);
        if (!el) {
            console.error(`[CUI Widget] Mount target "${config.mountTo}" not found`);
            return;
        }
        container = el as HTMLElement;
    } else if (config.embedded) {
        container = document.createElement('div');
        container.id = 'cui-widget-embed-root';
        container.style.position = 'fixed';
        container.style.inset = '0';
        container.style.zIndex = '2147483647';
        document.body.appendChild(container);
        createdContainer = true;
    } else {
        container = document.createElement('div');
        container.id = 'cui-widget-embed-root';
        container.style.position = 'fixed';
        container.style.width = '0';
        container.style.height = '0';
        container.style.overflow = 'visible';
        container.style.zIndex = '2147483647';
        document.body.appendChild(container);
        createdContainer = true;
    }

    root = createRoot(container);
    root.render(<CUIChatWidget {...config} />);

    window.CUIWidgetInfo = {
        variant: variant ? variant.name : 'legacy',
        version: PACKAGE_VERSION_TO_BE_REPLACED,
        integration: variant?.integration || 'direct',
    };

    window.CUIWidgetDestroy = () => {
        if (root) {
            root.unmount();
            root = null;
        }
        if (createdContainer) {
            container.remove();
        }
        delete window.CUIWidgetDestroy;
    };
}

/**
 * Mount the embed widget once the DOM is ready and the host page has
 * assigned window.CUIWidgetConfig (see wait-for-config.ts for why that can
 * happen after this script ran). Called by each embed entry after it has
 * registered its theme presets.
 */
export function mountEmbed(variant?: WidgetVariant) {
    currentVariant = variant;
    window.CUIWidgetRemount = () => mount(currentVariant);

    const boot = () => whenHostConfigReady(window, document, () => mount(variant));
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
    } else {
        boot();
    }
}
